
import { CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Icon,
  IconButton,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import { organizationRepository } from 'api/organizationRepository';
import { useAuth } from 'contexts/authContext';
import React, { useEffect, useRef, useState } from 'react';
import { MdAdd, MdDelete, MdEdit } from 'react-icons/md';

import { useLocation, useNavigate } from 'react-router-dom';
import AddPlantModal from 'views/admin/Organization/Component/AddPlantModal';
import EditPlantModal from 'views/admin/Organization/Component/EditPlantModal';
import DepartmentModal from "./DepartmentModal";
import EditDepartmentModal from './EditDepartmentModal';
import EditOrganizationModal from './EditOrganizationModal';
import LocationAdmin from 'views/admin/dashboards/locAdminDashboard';

export default function OrgDetailsPanel({ organization }: { organization: any }) {
  const cardBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const branchCardBg = useColorModeValue('gray.50', 'navy.700');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role } = useAuth();
  const isLeasedOrg = organization?.isThisLeased;
  const [parentOrgName, setParentOrgName] = useState<string>('');

  const { isOpen: isAddPlantOpen, onOpen: openAddPlantModal, onClose: closeAddPlantModal } = useDisclosure();
  const { isOpen: isEditBranchOpen, onOpen: openEditBranchModal, onClose: closeEditBranchModal } = useDisclosure();
  const { isOpen: isDeptModalOpen, onOpen: openDeptModal, onClose: closeDeptModal, } = useDisclosure();
  const { isOpen: isEditDeptOpen, onOpen: openEditDeptModal, onClose: closeEditDeptModal } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: openEditModal, onClose: closeEditModal, } = useDisclosure();

  const [branches, setBranches] = useState(organization?.locations || []);
  const [editBranch, setEditBranch] = useState<any | null>(null);
  const [editDept, setEditDept] = useState<any | null>(null);

  const [deleteDeptId, setDeleteDeptId] = useState<{ locationId: string; deptId: string } | null>(null);
  const [deleteBranchId, setDeleteBranchId] = useState<string | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  //Delete Organization
  const handleDeactivate = async () => {
    if (!organization?.id) return;
    try {
      await organizationRepository.deleteOrganization(organization.id);
      showCenteredAlert('Organization deactivated successfully', 'success');
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error: any) {
      const message =
        typeof error?.response?.data === 'string'
          ? error.response.data
          : error?.response?.data?.message ||
          'Failed to deactivate organization';

      showCenteredAlert(message, 'error');
    }
    finally {
      setConfirmDeactivate(false);
    }
  };

  useEffect(() => {
    const fetchParentOrg = async () => {
      try {
        if (organization?.parentOrgId) {
          const parent = await organizationRepository.getOrganizationById(
            organization.parentOrgId
          );
          setParentOrgName(parent.name);
        }
      } catch (error) {
      }
    };

    fetchParentOrg();
  }, [organization]);

  // Delete Location/Branch/Plant
  const handleDeleteBranch = async (locationId: string) => {
    if (role === "DeptUser") return;
    if (!organization?.id || !locationId) return;
    try {
      await organizationRepository.deleteBranch(organization.id, locationId);
      setBranches((prev: any[]) => prev.filter((b: any) => b.locationId !== locationId));
      showCenteredAlert('Plant deleted successfully', 'success');
    } catch (error: any) {
      const message =
        typeof error?.response?.data === 'string'
          ? error.response.data
          : error?.response?.data?.message ||
          'Failed to delete plant';

      showCenteredAlert(message, 'error');
    }
    finally {
      setDeleteBranchId(null);
    }
  };

  // Delete Department
  const handleDeleteDepartment = async (locationId: string, deptId: string) => {
    if (!organization?.id || !locationId || !deptId) return;
    try {
      await organizationRepository.deleteDepartment(organization.id, locationId, deptId);
      const updatedOrg = await organizationRepository.getOrganizationById(organization.id);
      setBranches(updatedOrg?.locations || []);
      showCenteredAlert('Department deleted successfully', 'success');

    } catch (error: any) {
      const message =
        typeof error?.response?.data === 'string'
          ? error.response.data
          : error?.response?.data?.message ||
          'Failed to delete department';

      showCenteredAlert(message, 'error');
    }
    finally {
      setDeleteDeptId(null);
    }
  };

  const visibleBranches =
    role === "LocationAdmin"
      ? branches.filter(
        (branch: any) => branch.locationId === user?.locationId
      )
      : branches;

  // Centered Alert
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success');
  const centerAlertTimerRef = useRef<number | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  const showCenteredAlert = (
    msg: string,
    status: 'success' | 'info' | 'warning' | 'error' = 'success'
  ) => {
    if (centerAlertTimerRef.current) {
      window.clearTimeout(centerAlertTimerRef.current);
      centerAlertTimerRef.current = null;
    }
    setCenterAlertMessage(msg);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
      centerAlertTimerRef.current = null;
    }, 3000);
  };

  const amcExpiryDate =
    organization?.hasAMC && organization?.createdTime
      ? new Date(
        new Date(organization.createdTime).setFullYear(
          new Date(organization.createdTime).getFullYear() + 1
        )
      ).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : null;
  return (
    <>

      <Box bg={cardBg} borderRadius="2xl" boxShadow="lg" p={{ base: 4, md: 6 }}>
        <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4}>
          <Text fontSize="2xl" fontWeight="700" color={textColor}>
            Organization Details: {organization?.name}
          </Text>
          <Flex justify="flex-end">
            <IconButton
              aria-label="Close"
              icon={<CloseIcon />}
              variant="ghost"
              size="sm"
              hidden={role === "OrgAdmin" || role === "LocationAdmin" || role === "DeptUser"}
              onClick={() => navigate('/admin/organizations/OrgList',
                {
                  state: location.state

                })}
            />
          </Flex>
        </Flex>

        <VStack spacing={8} align="stretch">
          {/* Org Info */}
          <Box
            bg={branchCardBg}
            p={6}
            borderRadius="xl"
            border="1px solid"
            borderColor={borderColor}
          >

            <Flex justify="space-between" align="center" mb={4} wrap="wrap">
              <Flex align="center" gap={2}>
                <Text fontSize="lg" fontWeight="700">
                  Organization Info
                </Text>

                <Badge
                  colorScheme={organization?.isActive ? "green" : "red"}
                  fontSize="0.75em"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {organization?.isActive ? "ACTIVE" : "INACTIVE"}
                </Badge>
              </Flex>

              <Flex gap={3}>
                {(role === "Admin" || role === "OrgAdmin") && (
                  <Button
                    leftIcon={<MdEdit />}
                    variant="darkBrand"
                    borderRadius="md"
                    size="sm"
                    onClick={openEditModal}
                  >
                    Edit
                  </Button>
                )}

                {role === "Admin" && (
                  <Button
                    leftIcon={<MdDelete />}
                    colorScheme="red"
                    borderRadius="md"
                    size="sm"
                    onClick={() => setConfirmDeactivate(true)}
                  >
                    Deactivate
                  </Button>
                )}
              </Flex>
            </Flex>
            {/* 2-2-2 Layout */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} fontSize="md" color={textColor}>

              {/* Column 1 */}
              <Box>
                <Text><strong>Name:</strong> {organization?.name}</Text>
                <Text mt={2}><strong>Email:</strong> {organization?.email}</Text>
                {organization?.isThisLeased && (
                  <Text mt={2}>
                    <strong>Parent Organization:</strong> {parentOrgName || 'Loading...'}
                  </Text>
                )}
              </Box>

              {/* Column 2 */}
              <Box>
                <Text><strong>Contact:</strong> {organization?.contact}</Text>
                <Text mt={2}><strong>GSTIN:</strong> {organization?.gsTin}</Text>
              </Box>

              {/* Column 3 */}
              <Box>
                <Text>
                  <strong>hasAMC:</strong> {organization?.hasAMC ? 'True' : 'False'}
                </Text>

                {organization?.hasAMC && (
                  <Text mt={2}>
                    <strong>AMC Expiry Date:</strong> {amcExpiryDate}
                  </Text>
                )}
              </Box>
            </SimpleGrid>
          </Box>

          {/* SHOW ONLY IF NOT LEASED */}
          {!isLeasedOrg && (
            <>
              {/* Plants Tile */}
              <Box
                bg={branchCardBg}
                p={6}
                borderRadius="xl"
                border="1px solid"
                borderColor={borderColor}
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="700">📍 Plants </Text>
                  {/* {role !== "DeptUser" && role !== "Vendor" && ( */}
                  {(role === "Admin") && (
                    <Button leftIcon={<MdAdd />} size="sm" colorScheme="blue" onClick={openAddPlantModal}>
                      Add Plant
                    </Button>
                  )}
                </Flex>

                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Name</Th>
                      <Th>Street</Th>
                      <Th>City</Th>
                      <Th>State</Th>
                      <Th>ZIP</Th>
                      <Th>Country</Th>
                      <Th>Status</Th>
                      {/* {role !== "DeptUser" && role !== "Vendor" && ( */}
                      {(role === "Admin"|| role === "OrgAdmin" || role=== "LocationAdmin" )&& (
                        <Th>Action</Th>
                      )}
                    </Tr>
                  </Thead>

                  <Tbody>
                    {visibleBranches.length > 0 ? (
                      visibleBranches.map((branch: any, i: number) => (
                        <React.Fragment key={i}>
                          {/* Plant Row */}
                          <Tr>
                            <Td>{branch.name}</Td>
                            <Td>{branch.address?.street}</Td>
                            <Td>{branch.address?.city}</Td>
                            <Td>{branch.address?.state}</Td>
                            <Td>{branch.address?.zipCode}</Td>
                            <Td>{branch.address?.country}</Td>

                            <Td>
                              <Badge
                                colorScheme={branch.isActive ? 'green' : 'red'}
                                fontSize="0.8em"
                                px={2}
                                borderRadius="full"
                              >
                                {branch.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </Td>

                            {(role === "Admin"|| role === "OrgAdmin" || role=== "LocationAdmin" )&& (
                              <Td>
                                <Flex gap={3}>
                                 { role === "Admin" && (
                                  <Tooltip label="Delete Plant">
                                    <span>
                                      <Icon
                                        as={MdDelete}
                                        w={5}
                                        h={5}
                                        color="red.500"
                                        cursor="pointer"
                                        _hover={{ color: 'red.600' }}
                                        onClick={() => setDeleteBranchId(branch.locationId)}
                                      />
                                    </span>
                                  </Tooltip>
                                 )}

                                  {/* {role !== "DeptUser" && role !== "Vendor"&& ( */}

                                  <Tooltip label="Edit Plant" hasArrow>
                                    <span>
                                      <Icon
                                        as={MdEdit}
                                        w={5}
                                        h={5}
                                        color="blue.500"
                                        cursor="pointer"
                                        _hover={{ color: 'blue.600' }}
                                        onClick={() => {
                                          setEditBranch(branch);
                                          openEditBranchModal();
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                </Flex>
                              </Td>
                            )}
                          </Tr>
                        </React.Fragment>
                      ))
                    ) : (
                      <Tr bg={branchCardBg}>
                        <Td colSpan={8} py={4}>
                          <Text fontSize="sm" color="gray.500">
                            Plants not added yet.
                            Plants will be listed here once added.
                          </Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>

              {/* Departments Tile */}
              <Box
                bg={branchCardBg}
                p={6}
                borderRadius="xl"
                border="1px solid"
                borderColor={borderColor}
              >
                <Flex justify="space-between" align="center" mb={4}>
                  <Text fontSize="lg" fontWeight="700">🏢 Departments </Text>

                  {role !== "DeptUser" && role !== "Vendor" && (
                    <Button leftIcon={<MdAdd />} size="sm" colorScheme="blue" onClick={openDeptModal}>
                      Add Department
                    </Button>
                  )}
                </Flex>

                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th>Department Name</Th>
                      <Th>Plant Name</Th>
                      {role !== "DeptUser" && role !== "Vendor" && (
                        <Th>Action</Th>
                      )}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visibleBranches.some((branch: any) => branch.departments?.length > 0) ? (
                      visibleBranches.flatMap((branch: any) =>
                        branch.departments?.map((dept: any, i: number) => (
                          <Tr key={`${branch.locationId}-${i}`}>
                            <Td>{dept.departmentName}</Td>
                            <Td>{branch.name}</Td>

                            {role !== "DeptUser" && role !== "Vendor" && (
                              <Td>
                                <Flex gap={3}>
                                  <Tooltip label="Delete Department">
                                    <span>
                                      <Icon
                                        as={MdDelete}
                                        w={5}
                                        h={5}
                                        color="red.500"
                                        cursor="pointer"
                                        _hover={{ color: 'red.600' }}
                                        onClick={() =>
                                          setDeleteDeptId({
                                            locationId: branch.locationId,
                                            deptId: dept.departmentId,
                                          })
                                        }
                                      />
                                    </span>
                                  </Tooltip>

                                  <Tooltip label="Edit Department">
                                    <span>
                                      <Icon
                                        as={MdEdit}
                                        w={5}
                                        h={5}
                                        color="blue.500"
                                        cursor="pointer"
                                        _hover={{ color: 'blue.600' }}
                                        onClick={() => {
                                          setEditDept({ ...dept, locationId: branch.locationId });
                                          openEditDeptModal();
                                        }}
                                      />
                                    </span>
                                  </Tooltip>
                                </Flex>
                              </Td>
                            )}
                          </Tr>
                        ))
                      )
                    ) : (
                      <Tr bg={branchCardBg}>
                        <Td colSpan={3} py={4}>
                          <Text fontSize="sm" color="gray.500">
                            Departments not added yet.
                            Departments will be listed here once added.
                          </Text>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            </>
          )}

          {/* Add Plant Modal */}
          <AddPlantModal
            isOpen={isAddPlantOpen}
            onClose={closeAddPlantModal}
            organizationId={organization.id}
            onPlantAdded={(updatedBranches) => setBranches(updatedBranches)}
            showCenteredAlert={showCenteredAlert}
          />
          {/* Add Department Modal */}
          <DepartmentModal
            isOpen={isDeptModalOpen}
            onClose={closeDeptModal}
            organizationId={organization.id}
            branches={branches}
            onDepartmentAdded={(updatedBranches) => setBranches(updatedBranches)}
            showCenteredAlert={showCenteredAlert}
          />

          {/* Edit Organization Modal */}
          <EditOrganizationModal
            isOpen={isEditModalOpen}
            onClose={closeEditModal}
            organization={organization}
            showCenteredAlert={showCenteredAlert}
            onUpdated={() => {
              window.location.reload();
            }}
          />

          {/* Edit Plant Modal */}
          <EditPlantModal
            isOpen={isEditBranchOpen}
            onClose={closeEditBranchModal}
            organizationId={organization.id}
            branch={editBranch}
            onBranchUpdated={(updated) => setBranches(updated)}
            showCenteredAlert={showCenteredAlert}
          />

          {/* Edit Department Modal */}
          <EditDepartmentModal
            isOpen={isEditDeptOpen}
            onClose={closeEditDeptModal}
            department={editDept}
            organizationId={organization.id}
            branches={branches}
            onDepartmentUpdated={setBranches}
            showCenteredAlert={showCenteredAlert}
          />


          {/* Centered Alert */}
          {centerAlertVisible && (
            <Center
              position="fixed"
              top="0"
              left="0"
              right="0"
              bottom="0"
              zIndex={60}
              pointerEvents="none"
            >
              <Alert
                status={centerAlertStatus}
                variant="solid"
                bg={
                  centerAlertMessage.toLowerCase().includes("delete") ||
                    centerAlertMessage.toLowerCase().includes("deactivate")
                    ? "red.500"
                    : undefined
                }
                color={
                  centerAlertMessage.toLowerCase().includes("delete") ||
                    centerAlertMessage.toLowerCase().includes("deactivate")
                    ? "white"
                    : undefined
                }
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                width="600px"
                borderRadius="14px"
                boxShadow="xl"
                py={10}
                px={16}
                pointerEvents="auto"
              >
                <AlertIcon
                  boxSize="40px"
                  mr={0}
                  color={
                    centerAlertMessage.toLowerCase().includes("delete") ||
                      centerAlertMessage.toLowerCase().includes("deactivate")
                      ? "white"
                      : undefined
                  }
                />

                <Text fontSize="lg" fontWeight="700" mt={1}>
                  {centerAlertMessage}
                </Text>
              </Alert>
            </Center>
          )}
          <AlertDialog
            isOpen={!!deleteBranchId}
            leastDestructiveRef={cancelRef}
            onClose={() => setDeleteBranchId(null)}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Delete Plant
                </AlertDialogHeader>
                <AlertDialogBody>
                  Are you sure you want to delete this Plant? This action cannot be undone.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={() => setDeleteBranchId(null)}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={() => deleteBranchId && handleDeleteBranch(deleteBranchId)}
                    ml={3}
                  >
                    Delete
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

          <AlertDialog
            isOpen={confirmDeactivate}
            leastDestructiveRef={cancelRef}
            onClose={() => setConfirmDeactivate(false)}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Deactivate Organization
                </AlertDialogHeader>
                <AlertDialogBody>
                  Are you sure you want to deactivate this organization? This action cannot be undone.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={() => setConfirmDeactivate(false)}>
                    Cancel
                  </Button>
                  <Button colorScheme="red" onClick={handleDeactivate} ml={3}>
                    Deactivate
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

          <AlertDialog
            isOpen={!!deleteDeptId}
            leastDestructiveRef={cancelRef}
            onClose={() => setDeleteDeptId(null)}
            isCentered
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  Delete Department
                </AlertDialogHeader>
                <AlertDialogBody>
                  Are you sure you want to delete this department? This action cannot be undone.
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={() => setDeleteDeptId(null)}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    onClick={() =>
                      deleteDeptId &&
                      handleDeleteDepartment(deleteDeptId.locationId, deleteDeptId.deptId)
                    }
                    ml={3}
                  >
                    Delete
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>

        </VStack>
      </Box>
    </>
  );
}